import { useMemo } from 'react'

export interface UseGroupedDataProps {
  data: any[]
  groupByFields: string[]
  expandedGroups: Record<string, boolean>
  parentInfoFields?: string[]
  getRowId?: (row: any) => string | number
}

export function useGroupedData({
  data,
  groupByFields,
  expandedGroups,
  parentInfoFields = [],
  getRowId
}: UseGroupedDataProps) {
  // Get row ID, prioritize custom getRowId
  const getItemId = (item: any): string | number => {
    if (getRowId) {
      return getRowId(item)
    }
    return item.id || `id-${Math.random().toString(36).substring(2)}`
  }

  // Process data into flat structure with grouping information
  const processedRows = useMemo(() => {
    if (!data || data.length === 0) return []

    // Group by fields
    const groupData = (
      items: any[],
      fields: string[],
      level = 0,
      parentPath: string[] = []
    ): any[] => {
      if (level >= fields.length) return []

      const field = fields[level]
      const groups: Record<string, any> = {}

      // Create groups
      items.forEach(item => {
        const groupValue = item[field]?.toString() || 'N/A'
        const groupKey =
          [...parentPath, groupValue].join('-') ||
          `empty-${Math.random().toString(36).substring(2)}`
        if (!groups[groupKey]) {
          // create group row object
          const groupRow: Record<string, any> = {
            id: `group-${groupKey}`,
            isGroupRow: true,
            groupValue,
            groupField: field,
            level,
            childCount: 0,
            children: []
          }

          // copy all original data fields, initialize to empty values
          Object.keys(item).forEach(key => {
            if (key !== 'id') groupRow[key] = ''
          })

          groups[groupKey] = groupRow
        }

        groups[groupKey].childCount++
        groups[groupKey].children.push({ ...item }) // create deep copy of item to avoid reference issues
      })

      // Convert to array and process next level recursively
      let result: any[] = []

      Object.entries(groups).forEach(([_, group]) => {
        // create deep copy of group to avoid reference issues
        const groupCopy = { ...group, children: [...group.children] }

        // set the value of the parent info fields for each group
        if (groupCopy.children.length > 0) {
          // find the first item that contains all parent info fields
          const firstItemWithInfo =
            groupCopy.children.find((item: Record<string, any>) =>
              parentInfoFields.every(infoField => item[infoField] !== undefined)
            ) || groupCopy.children[0]

          if (firstItemWithInfo) {
            // 复制所有字段数据，包括嵌套对象
            Object.keys(firstItemWithInfo).forEach(key => {
              // 跳过id和已经设置的基础属性
              if (
                !['id'].includes(key) &&
                typeof firstItemWithInfo[key] !== 'undefined'
              ) {
                groupCopy[key] = firstItemWithInfo[key]
              }
            })

            // 确保parentInfoFields的值被正确设置
            parentInfoFields.forEach(infoField => {
              if (typeof firstItemWithInfo[infoField] !== 'undefined') {
                groupCopy[infoField] = firstItemWithInfo[infoField]
              }
            })
          }
        }

        result.push(groupCopy)

        // If group is expanded and there are more grouping fields
        if (expandedGroups[groupCopy.id] && level < fields.length - 1) {
          // Add child groups
          const childGroups = groupData(groupCopy.children, fields, level + 1, [
            ...parentPath,
            groupCopy.groupValue
          ])
          result = result.concat(childGroups)
        }
        // If it's the last level group and expanded, add data rows
        else if (expandedGroups[groupCopy.id] && level === fields.length - 1) {
          const childRows = groupCopy.children.map((item: any) => {
            const itemId = getItemId(item)
            const childRow = {
              ...item,
              id: `child-${itemId}-${groupCopy.id}`, // ensure unique child row ID
              isChildRow: true,
              parentGroupId: groupCopy.id,
              level: level + 1,
              // Store the parent group field to hide it in child rows
              parentGroupField: groupCopy.groupField
            }
            return childRow
          })

          result = result.concat(childRows)
        }
      })

      return result
    }

    return groupData(data, groupByFields, 0)
  }, [data, groupByFields, expandedGroups, getRowId, ...parentInfoFields])

  return processedRows
}
